import React, { useEffect, useState } from 'react';

import Button from '../../components/ui/Button';
import Footer from '../homepage/components/Footer';
import Header from '../../components/ui/Header';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import { careersService } from '../../services/careersService';

const CareersPage = () => {
	const [careers, setCareers] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		loadCareers();
	}, []);

	const loadCareers = async () => {
		try {
			setLoading(true);
			setError(null);
			const { data, error } = await careersService.getCareers(false); // public mode
			if (error) throw error;
			setCareers(Array.isArray(data) ? data : []);
		} catch (error) {
			console.error('Error loading careers:', error);
			setError('Failed to load career opportunities. Please try again later.');
			setCareers([]);
		} finally {
			setLoading(false);
		}
	};

	return (
		<>
			<Helmet>
				<title>Careers - Fepa Law Partners | Join Our Legal Team</title>
				<meta
					name='description'
					content='Join Fepa Law Partners dynamic legal team in Nigeria. Explore career opportunities for attorneys, and support staff.'
				/>
				<meta
					name='keywords'
					content='legal careers Nigeria, attorney jobs Port Harcourt, law firm careers, legal employment Nigeria'
				/>
			</Helmet>

			<div className='min-h-screen bg-gray-50'>
				<Header />

				{/* Hero Section */}
				<section className='relative bg-gradient-to-r from-[#800020] to-[#800020]/80 pt-20 pb-16'>
					<div className='absolute inset-0 bg-black/20'></div>
					<div className='relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
						<div className='text-center'>
							<div className='inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6'>
								<Icon name='Briefcase' size={20} color='white' />
								<span className='text-white font-source font-medium'>Join Our Legal Team</span>
							</div>
							<h1 className='font-playfair font-bold text-4xl lg:text-6xl text-white mb-6'>
								Build Your Career
								<span className='block text-[#DB954E]'>with Fepa Law Partners</span>
							</h1>
							<p className='text-xl text-white/90 max-w-3xl mx-auto leading-relaxed'>
								Join our team of dedicated legal professionals and make a meaningful impact while
								advancing your career in a supportive, collaborative environment.
							</p>
						</div>
					</div>
				</section>

				{/* Careers Section */}
				<section className='py-16 bg-white'>
					<div className='max-w-7xl mx-auto px-4 lg:px-8'>
						{loading ? (
							<div className='flex items-center justify-center py-16'>
								<Icon name='Loader' className='h-8 w-8 animate-spin text-[#800020]' />
								<span className='ml-2 text-gray-600'>Loading career opportunities...</span>
							</div>
						) : error ? (
							<div className='text-center py-16'>
								<div className='w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-8'>
									<Icon name='AlertCircle' size={48} color='#EF4444' />
								</div>
								<h2 className='text-2xl font-playfair font-bold text-[#800020] mb-4'>
									Unable to Load Positions
								</h2>
								<p className='text-gray-700 font-source mb-8'>{error}</p>
								<button
									onClick={loadCareers}
									className='bg-[#800020] text-white px-6 py-3 rounded-lg hover:bg-[#6b001a] transition-colors'
								>
									Try Again
								</button>
							</div>
						) : careers.length === 0 ? (
							<div className='text-center py-16'>
								{/* Icon */}
								<div className='w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-8'>
									<Icon name='Briefcase' size={48} color='#6B7280' />
								</div>

								{/* Title */}
								<h2 className='text-3xl md:text-4xl font-playfair font-bold text-[#800020] mb-4'>
									No Open Positions
								</h2>

								{/* Description */}
								<p className='text-lg text-gray-700 font-source max-w-2xl mx-auto leading-relaxed mb-8'>
									We currently do not have any open positions available. However, we're always
									interested in connecting with talented legal professionals who share our
									commitment to excellence.
								</p>

								{/* Additional Info */}
								<div className='bg-gray-50 rounded-xl p-6 mb-8'>
									<h3 className='font-playfair font-bold text-xl text-[#800020] mb-4'>
										Future Opportunities
									</h3>
									<p className='text-gray-700 font-source leading-relaxed'>
										We regularly review our staffing needs and may have opportunities in the
										following areas:
									</p>
									<div className='grid grid-cols-1 md:grid-cols-2 gap-4 mt-4'>
										<div className='flex items-center space-x-3'>
											<Icon name='Scale' size={20} color='#800020' />
											<span className='font-source text-gray-700'>Associate Attorneys</span>
										</div>
										<div className='flex items-center space-x-3'>
											<Icon name='Users' size={20} color='#800020' />
											<span className='font-source text-gray-700'>Legal Assistants</span>
										</div>
										<div className='flex items-center space-x-3'>
											<Icon name='GraduationCap' size={20} color='#800020' />
											<span className='font-source text-gray-700'>Law Interns</span>
										</div>
									</div>
								</div>
							</div>
						) : (
							<div>
								<div className='text-center mb-12'>
									<h2 className='text-3xl md:text-4xl font-playfair font-bold text-[#800020] mb-4'>
										Open Positions
									</h2>
									<p className='text-lg text-gray-700 font-source max-w-2xl mx-auto'>
										Explore our current career opportunities and find the perfect role to advance
										your legal career.
									</p>
								</div>

								<div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
									{careers.map((career) => (
										<div
											key={career.id}
											className='bg-white border border-gray-200 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300'
										>
											<div className='flex items-start justify-between mb-4'>
												<div className='flex-1'>
													<h3 className='text-xl font-playfair font-bold text-[#800020] mb-2'>
														{career.title}
													</h3>
													<div className='flex items-center space-x-4 text-sm text-gray-600 mb-3'>
														<span className='flex items-center space-x-1'>
															<Icon name='Building' size={16} />
															<span>{career.department}</span>
														</span>
														<span className='flex items-center space-x-1'>
															<Icon name='MapPin' size={16} />
															<span>{career.location}</span>
														</span>
													</div>
													<div className='flex items-center space-x-4 text-sm text-gray-600 mb-4'>
														<span className='bg-[#800020]/10 text-[#800020] px-2 py-1 rounded-full text-xs font-medium'>
															{career.employment_type.replace('_', ' ')}
														</span>
														{career.salary_range && (
															<span className='text-gray-600'>{career.salary_range}</span>
														)}
														{career.experience_level && (
															<span className='text-gray-600'>{career.experience_level}</span>
														)}
													</div>
												</div>
												{career.is_featured && (
													<span className='bg-[#DB954E] text-white px-2 py-1 rounded-full text-xs font-medium'>
														Featured
													</span>
												)}
											</div>

											<p className='text-gray-700 font-source leading-relaxed mb-4 line-clamp-3'>
												{career.description}
											</p>

											{career.requirements && career.requirements.length > 0 && (
												<div className='mb-4'>
													<h4 className='font-playfair font-semibold text-[#800020] mb-2'>
														Key Requirements:
													</h4>
													<ul className='text-sm text-gray-600 space-y-1'>
														{career.requirements.slice(0, 3).map((req, index) => (
															<li key={index} className='flex items-start space-x-2'>
																<Icon
																	name='Check'
																	size={14}
																	className='text-green-600 mt-0.5 flex-shrink-0'
																/>
																<span>{req}</span>
															</li>
														))}
														{career.requirements.length > 3 && (
															<li className='text-gray-500 italic'>
																+{career.requirements.length - 3} more requirements
															</li>
														)}
													</ul>
												</div>
											)}

											<div className='flex items-center justify-between pt-4 border-t border-gray-200'>
												<div className='text-sm text-gray-600'>
													{career.application_deadline ? (
														<span>
															Apply by {new Date(career.application_deadline).toLocaleDateString()}
														</span>
													) : (
														<span>Open until filled</span>
													)}
												</div>
												<a
													href={`mailto:info@fepalawpartners.com?subject=Application for ${career.title}`}
													className='bg-[#800020] text-white px-4 py-2 rounded-lg hover:bg-[#6b001a] transition-colors text-sm font-medium'
												>
													Apply Now
												</a>
											</div>
										</div>
									))}
								</div>
							</div>
						)}
					</div>
				</section>

				{/* Why Work With Us Section */}
				<section className='py-16 bg-gray-50'>
					<div className='max-w-7xl mx-auto px-4 lg:px-8'>
						<div className='text-center mb-12'>
							<h2 className='text-3xl md:text-4xl font-playfair font-bold text-[#800020] mb-4'>
								Why Choose Fepa Law Partners?
							</h2>
							<p className='text-lg text-gray-700 font-source max-w-2xl mx-auto'>
								We believe in fostering talent, encouraging innovation, and maintaining a healthy
								work-life balance for all our team members.
							</p>
						</div>

						<div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
							{/* Career Growth */}
							<div className='text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300'>
								<div className='w-16 h-16 bg-[#800020]/10 rounded-full flex items-center justify-center mx-auto mb-4'>
									<Icon name='TrendingUp' size={32} color='#800020' />
								</div>
								<h3 className='font-playfair font-bold text-xl text-[#800020] mb-3'>
									Career Growth
								</h3>
								<p className='text-gray-700 font-source leading-relaxed'>
									Clear advancement paths, mentorship programs, and continuous learning
									opportunities to help you reach your professional goals.
								</p>
							</div>

							{/* Collaborative Culture */}
							<div className='text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300'>
								<div className='w-16 h-16 bg-[#DB954E]/10 rounded-full flex items-center justify-center mx-auto mb-4'>
									<Icon name='Users' size={32} color='#DB954E' />
								</div>
								<h3 className='font-playfair font-bold text-xl text-[#800020] mb-3'>
									Collaborative Culture
								</h3>
								<p className='text-gray-700 font-source leading-relaxed'>
									Work alongside experienced professionals in a supportive environment that
									encourages teamwork and knowledge sharing.
								</p>
							</div>

							{/* Work-Life Balance */}
							<div className='text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300'>
								<div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4'>
									<Icon name='Heart' size={32} color='#10B981' />
								</div>
								<h3 className='font-playfair font-bold text-xl text-[#800020] mb-3'>
									Work-Life Balance
								</h3>
								<p className='text-gray-700 font-source leading-relaxed'>
									Flexible work arrangements, comprehensive benefits, and a culture that values
									personal well-being alongside professional excellence.
								</p>
							</div>
						</div>
					</div>
				</section>

				{/* Call to Action */}
				<section className='py-16 bg-gradient-to-r from-[#800020] to-[#800020]/90 text-white'>
					<div className='max-w-4xl mx-auto px-4 lg:px-8 text-center'>
						<h2 className='text-3xl md:text-4xl font-playfair font-bold mb-4'>Stay Connected</h2>
						<p className='text-lg text-white/90 mb-8 max-w-2xl mx-auto'>
							Don't see the perfect position? We're always interested in connecting with talented
							legal professionals. Send us your resume and tell us about your career goals.
						</p>
						<div className='flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4'>
							<a
								href='mailto:info@fepalawpartners.com?subject=General Application - Legal Professional'
								className='inline-flex items-center space-x-3 bg-[#DB954E] hover:bg-[#DB954E]/90 text-white px-8 py-4 rounded-lg font-source font-semibold shadow-lg hover:shadow-xl transition-all duration-300'
							>
								<Icon name='Mail' size={18} />
								<span>Submit General Application</span>
							</a>
						</div>
					</div>
				</section>

				<Footer />
			</div>
		</>
	);
};

export default CareersPage;
